import React, { useContext, useEffect } from "react";
import utils from "../../../../../../utils";
import values from "../../../../../../values";
import Context from "../../../../../../context";
import Cookies from "js-cookie";
import TitleLine from "../../../../../../components/TitleLine";

import {
  ICityItem,
  IItem,
  IProvinceItem,
} from "../../../../../../types/common.interface";
import { useFormik } from "formik";
import { ICreateAndUpdateDriver } from "../../../../../../types/driver.interface";
import {
  CustomButton2,
  CustomDropdownSearch,
  CustomInput,
  CustomView,
} from "../../../../../../components";

import * as Yup from "yup";
import { useQuery } from "react-query";
import api from "../../../../../../api";
import validations from "../../../../../../validations";
import { useTranslation } from "react-i18next";
import { gender, marital } from "../../../../../../values/dropdownItems";

type Props = {
  defaultValues: any;
  onSubmit: (event: ICreateAndUpdateDriver) => void;
};

const CompanyDriverInformation = (props: Props) => {
  const { isLoading, setShowModal } = React.useContext(Context.DriverScreen);
  const [selectedGender, setSelectedGender] = React.useState<IItem | undefined>(
    gender.filter((item) => {
      return item.value === props?.defaultValues?.gender;
    })[0]
  );
  const [selectedMarital, setSelectedMarital] = React.useState<
    IItem | undefined
  >(props?.defaultValues?.marital);
  const companyIdentify = Cookies.get(values.constants.USER.IDENTIFY);
  const [selectedCity, setSelectedCity] = React.useState<ICityItem>();
  const [selectedProvince, setSelectedProvince] =
    React.useState<IProvinceItem>();
  const [dropDownErrors, setDropDownErrors] = React.useState<any>({
    province: "",
    city: "",
    gender: "",
    marital: "",
  });

  // !! ===========================================================================================================
  // !!                                            Start API Call
  // !! -----------------------------------------------------------------------------------------------------------

  const getAllProvince = useQuery([values.useQueryKeys.getAllProvince], () =>
    api.get.common.getAllProvince()
  );
  const getAllCity = useQuery(
    [values.useQueryKeys.getAllCity, selectedProvince],
    () =>
      api.get.common.getAllCity({
        provinceCode: selectedProvince
          ? selectedProvince?.Code
          : getAllProvince?.data?.result?.find(
              (item: IProvinceItem) =>
                item?._id ===
                props.defaultValues?.schoolDriverInformation?.province
            ).Code,
      })
  );

  useEffect(() => {
    setSelectedProvince(
      getAllProvince?.data?.result?.find(
        (item: IProvinceItem) =>
          item?._id === props.defaultValues?.schoolDriverInformation?.province
      )
    );
  }, [props.defaultValues, getAllProvince?.data?.result]);

  useEffect(() => {
    setSelectedCity(
      getAllCity?.data?.result?.find(
        (item: ICityItem) =>
          item?._id === props.defaultValues?.schoolDriverInformation?.city
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAllCity?.data?.result]);

  // ?? ===========================================================================================================
  // ??                                           Use Formik Function
  // ?? -----------------------------------------------------------------------------------------------------------

  const formik = useFormik<ICreateAndUpdateDriver>({
    enableReinitialize: true,
    initialValues: {
      phoneNumber: props?.defaultValues?.phoneNumber,
      firstName: props?.defaultValues?.firstName,
      lastName: props?.defaultValues?.lastName,
      nationalCode: props?.defaultValues?.driverInformation?.nationalCode,
      kartNumber: props?.defaultValues?.financial?.kartNumber,
      address: props?.defaultValues?.schoolDriverInformation?.address,
      shabaNumber: props?.defaultValues?.financial?.shabaNumber,
    },
    validationSchema: Yup.object({
      address: validations.yupValidations.address,
      lastName: validations.yupValidations.lastName,
      firstName: validations.yupValidations.firstName,
      kartNumber: validations.yupValidations.kartNumber,
      phoneNumber: validations.yupValidations.phoneNumber,
      shabaNumber: validations.yupValidations.shabaNumber,
      nationalCode: validations.yupValidations.nationalCode,
    }),
    onSubmit: (values: ICreateAndUpdateDriver, { setSubmitting }) => {
      if (!companyIdentify) return utils.errorHandler(9013);
      const formValues: ICreateAndUpdateDriver = {
        ...values,
        gender: selectedGender,
        company: { _id: companyIdentify },
        marital: selectedMarital,
        city: selectedCity?._id,
        province: selectedProvince?._id,
      };

      if (
        selectedProvince &&
        selectedCity &&
        selectedMarital &&
        selectedGender
      ) {
        Promise.resolve(props.onSubmit(formValues)).catch((e) =>
          setSubmitting(false)
        );
      }
    },
  });

  const { t } = useTranslation();

  const submitHandler = () => {
    let dropDownErrorsList = {
      province: "",
      city: "",
      gender: "",
      marital: "",
      company: "",
    };
    if (!selectedGender) {
      dropDownErrorsList = {
        ...dropDownErrorsList,
        gender: t("gender_is_required"),
      };
    }
    if (!selectedMarital) {
      dropDownErrorsList = {
        ...dropDownErrorsList,
        marital: t("marital_is_required"),
      };
    }
    if (!selectedProvince) {
      dropDownErrorsList = {
        ...dropDownErrorsList,
        province: t("province_is_required"),
        city: t("city_is_required"),
      };
    } else if (!selectedCity) {
      dropDownErrorsList = {
        ...dropDownErrorsList,
        city: t("city_is_required"),
      };
    }

    setDropDownErrors(dropDownErrorsList);

    formik.submitForm();
  };

  const { userInfo } = useContext(Context.UserScreen);

  const provinceFiltered = getAllProvince?.data?.result?.filter((item: any) =>
    userInfo?.companyInformation?.province?.includes(item?._id)
  );
  const cityFiltered = getAllCity?.data?.result?.filter((item: any) =>
    userInfo?.companyInformation?.city?.includes(item?._id)
  );

  // ?? ===========================================================================================================
  // ??                                              JSX Content
  // ?? -----------------------------------------------------------------------------------------------------------

  return (
    <form onSubmit={submitHandler}>
      <CustomView.Container className="update-form-content">
        <TitleLine title={t("driverInfo")} />
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="firstName"
              id="firstName"
              label={t("firstName")}
              value={formik.values.firstName}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.firstName && formik.touched.firstName}
              errorText={formik.errors.firstName}
            />
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="lastName"
              id="lastName"
              label={t("lastName")}
              value={formik.values.lastName}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.lastName && formik.touched.lastName}
              errorText={formik.errors.lastName}
            />
          </CustomView.Column>
        </CustomView.Row>
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="nationalCode"
              id="nationalCode"
              label={t("nationalCode")}
              value={formik.values.nationalCode}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.nationalCode && formik.touched.nationalCode}
              errorText={formik.errors.nationalCode}
              maxLength={10}
            />
          </CustomView.Column>

          <CustomView.Column medium={6} className="form-colum dropdown">
            <CustomInput
              id="phoneNumber"
              name="phoneNumber"
              label={t("phoneNumber")}
              value={formik.values.phoneNumber}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.phoneNumber && formik.touched.phoneNumber}
              errorText={formik.errors.phoneNumber}
              maxLength={11}
            />
          </CustomView.Column>
        </CustomView.Row>

        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum dropdown">
            <label className="form-label">{t("province")}</label>
            <CustomDropdownSearch
              items={provinceFiltered}
              itemName="Name"
              defaultItem={selectedProvince}
              onSelectItem={(event: IProvinceItem) => {
                setSelectedCity(undefined);
                setSelectedProvince(event);
                setDropDownErrors({ ...dropDownErrors, province: "" });
              }}
              placeHolder={t("selectProvince")}
              isSearch
            />
            <div className="error">
              {dropDownErrors.province !== "" && dropDownErrors.province}
            </div>
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum dropdown">
            <label className="form-label">{t("city")}</label>
            <CustomDropdownSearch
              items={cityFiltered}
              itemName="Name"
              defaultItem={selectedCity}
              onSelectItem={(event: ICityItem) => {
                setSelectedCity(event);
                setDropDownErrors({ ...dropDownErrors, city: "" });
              }}
              placeHolder={t("selectCity")}
              isSearch
            />
            <div className="error">
              {dropDownErrors.city !== "" && dropDownErrors.city}
            </div>
          </CustomView.Column>
        </CustomView.Row>

        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum dropdown">
            <label className="form-label">{t("gender")}</label>

            <CustomDropdownSearch
              items={gender.map((item) => {
                return { ...item, name: t(item.name as string) };
              })}
              itemName="name"
              placeHolder={t("selectGender")}
              defaultItem={selectedGender}
              onSelectItem={(event: any) => {
                setSelectedGender(event);
                setDropDownErrors({ ...dropDownErrors, gender: "" });
              }}
            />
            <div className="error">
              {dropDownErrors.gender !== "" && dropDownErrors.gender}
            </div>
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum dropdown">
            <label className="form-label">{t("marital")}</label>

            <CustomDropdownSearch
              items={marital.map((item) => {
                return { ...item, name: t(item.name as string) };
              })}
              itemName="name"
              placeHolder={t("selectMarital")}
              defaultItem={selectedMarital}
              onSelectItem={(event: any) => {
                setSelectedMarital(event);
                setDropDownErrors({ ...dropDownErrors, marital: "" });
              }}
            />
            <div className="error">
              {dropDownErrors.marital !== "" && dropDownErrors.marital}
            </div>
          </CustomView.Column>
        </CustomView.Row>

        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="kartNumber"
              id="kartNumber"
              label={t("cardNumber")}
              value={formik.values.kartNumber}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.kartNumber && formik.touched.kartNumber}
              errorText={formik.errors.kartNumber}
              maxLength={16}
            />
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="shabaNumber"
              id="shabaNumber"
              label={t("shabaNumber")}
              value={formik.values.shabaNumber}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.shabaNumber && formik.touched.shabaNumber}
              errorText={formik.errors.shabaNumber}
              maxLength={24}
              isShaba
            />
          </CustomView.Column>
        </CustomView.Row>
        <CustomView.Row className="form-row">
          <CustomView.Column medium={12} className="form-colum">
            <CustomInput
              name="address"
              id="address"
              label={t("address")}
              value={formik.values.address}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.address && formik.touched.address}
              errorText={formik.errors.address}
            />
          </CustomView.Column>
        </CustomView.Row>
        <CustomView.Row className="form-row">
          <CustomView.Column
            large={3}
            medium={4}
            small={6}
            className="form-colum"
          >
            <CustomButton2
              isLoading={isLoading}
              onClick={() => setShowModal!("NONE")}
              title={t("cancel")}
              type="danger"
            />
          </CustomView.Column>
          <CustomView.Column
            large={3}
            medium={4}
            small={6}
            className="form-colum"
          >
            <CustomButton2
              isLoading={isLoading}
              onClick={submitHandler}
              title={t("next")}
            />
          </CustomView.Column>
        </CustomView.Row>
      </CustomView.Container>
    </form>
  );
};

export default CompanyDriverInformation;
